import { useEffect, useMemo, useRef, useState } from 'react';
import {
  ANALYTES,
  INDICATORS,
  LIMITS,
  type CurvePoint,
  equivalenceVolume,
  indicatorColor,
  prettyFormula,
  suitableIndicators,
  titrationCurve,
  titrationState,
} from '@chemai/engine';
import { type MessageKey, useI18n } from '../../i18n';
import { PhChart } from '../../components/PhChart';
import { NumberField, SaveButton, SelectField, SimBadge, Stat } from '../../components/ui';
import { progress } from '../../lib/progress';

type AnalyteId = 'HCl' | 'CH3COOH' | 'NaOH' | 'NH3';
type IndicatorId = 'phenolphthalein' | 'methylOrange' | 'bromothymolBlue';

function colourKey(ind: IndicatorId, pH: number): MessageKey {
  if (ind === 'phenolphthalein') return pH < 8.2 ? 'colour.colourless' : pH < 9 ? 'colour.paleP' : 'colour.pink';
  if (ind === 'methylOrange') return pH < 3.1 ? 'colour.red' : pH <= 4.4 ? 'colour.orange' : 'colour.yellow';
  return pH < 6 ? 'colour.yellow' : pH <= 7.6 ? 'colour.green' : 'colour.blue';
}

const round2 = (v: number) => Math.round(v * 100) / 100;

export function TitrationSim() {
  const { t, num } = useI18n();
  const [analyteId, setAnalyteId] = useState<AnalyteId>('HCl');
  const [analyteConc, setAnalyteConc] = useState(0.1);
  const [analyteVolume, setAnalyteVolume] = useState(25);
  const [titrantConc, setTitrantConc] = useState(0.1);
  const [indicator, setIndicator] = useState<IndicatorId>('phenolphthalein');
  const [added, setAdded] = useState(0);
  const [recorded, setRecorded] = useState<CurvePoint[]>([]);
  const [showTheory, setShowTheory] = useState(false);
  const [revealVe, setRevealVe] = useState(false);
  const [running, setRunning] = useState(false);
  const [drop, setDrop] = useState(0);

  const setup = useMemo(() => ({ analyteId, analyteConc, analyteVolume, titrantConc }), [analyteId, analyteConc, analyteVolume, titrantConc]);
  const analyte = ANALYTES[analyteId];
  const ve = equivalenceVolume(setup);
  const maxV = Math.min(LIMITS.buretteCapacity, Math.max(10, Math.ceil((ve * 2) / 5) * 5));
  const state = useMemo(() => titrationState(setup, added), [setup, added]);
  const theory = useMemo(() => (showTheory ? titrationCurve(setup, maxV) : null), [showTheory, setup, maxV]);
  const suitable = useMemo(() => suitableIndicators(setup), [setup]);

  // a new set-up empties the flask
  useEffect(() => {
    setAdded(0);
    setRecorded([{ v: 0, pH: titrationState(setup, 0).pH }]);
    setRunning(false);
  }, [setup]);

  const addedRef = useRef(added);
  addedRef.current = added;

  const setVolume = (v: number) => {
    const next = round2(Math.min(maxV, Math.max(0, v)));
    if (next === addedRef.current) return;
    setAdded(next);
    setDrop((d) => d + 1);
    setRecorded((r) => [...r.filter((p) => Math.abs(p.v - next) > 1e-9), { v: next, pH: titrationState(setup, next).pH }].sort((a, b) => a.v - b.v));
  };

  // automatic run: big steps far from equivalence, single drops near it
  useEffect(() => {
    if (!running) return;
    const h = setInterval(() => {
      const v = addedRef.current;
      if (v >= maxV) { setRunning(false); return; }
      const step = Math.abs(v - ve) < 1.2 ? 0.05 : Math.abs(v - ve) < 3 ? 0.2 : 0.5;
      setVolume(v + step);
    }, 90);
    return () => clearInterval(h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [running, maxV, ve, setup]);

  const ind = INDICATORS[indicator];
  const liquid = indicatorColor(ind, state.pH);
  const isBase = analyte.kind.endsWith('base');
  const weak = analyte.kind.startsWith('weak');
  const regionKey: MessageKey =
    state.region === 'before-equivalence' && isBase ? 'tit.region.before-equivalence.base' : (`tit.region.${state.region}` as MessageKey);

  // apparatus geometry
  const buretteTop = 30;
  const buretteBottom = 250;
  const level = buretteTop + (added / LIMITS.buretteCapacity) * (buretteBottom - buretteTop);
  const fill = Math.min(1, state.totalVolume / 150);
  const liquidTop = 405 - 15 - fill * 70;

  const excessMmol = Math.abs(state.analyteMmol - state.titrantMmol);

  return (
    <div className="sim">
      <SimBadge />
      <div className="sim-grid">
        <section className="panel" aria-labelledby="tit-setup">
          <h3 id="tit-setup">{t('tit.setup')}</h3>
          <SelectField<AnalyteId>
            label={t('tit.analyte')}
            value={analyteId}
            onChange={setAnalyteId}
            options={(['HCl', 'CH3COOH', 'NaOH', 'NH3'] as AnalyteId[]).map((id) => ({ value: id, label: t(`analyte.${id}` as MessageKey) }))}
          />
          <NumberField label={t('tit.analyteConc')} value={analyteConc} onChange={setAnalyteConc} min={LIMITS.conc.min} max={LIMITS.conc.max} step={0.01} slider />
          <NumberField label={t('tit.analyteVolume')} value={analyteVolume} onChange={setAnalyteVolume} min={LIMITS.analyteVolume.min} max={LIMITS.analyteVolume.max} step={1} slider />
          <p className="muted">{t('tit.titrant')}: <strong>{prettyFormula(analyte.titrant)}</strong></p>
          <NumberField label={t('tit.titrantConc')} value={titrantConc} onChange={setTitrantConc} min={LIMITS.conc.min} max={LIMITS.conc.max} step={0.01} slider />
          <SelectField<IndicatorId>
            label={t('tit.indicator')}
            value={indicator}
            onChange={setIndicator}
            options={(Object.keys(INDICATORS) as IndicatorId[]).map((id) => ({ value: id, label: t(`ind.${id}` as MessageKey) }))}
          />
          <p className="hint">{t('tit.suggested', { list: suitable.map((s) => t(`ind.${s}` as MessageKey).split(' (')[0]).join(', ') })}</p>
          {!suitable.includes(indicator) && <p className="warn" role="status">{t('tit.notSuitable')}</p>}
          {ve > LIMITS.buretteCapacity && <p className="warn" role="alert">{t('tit.burette.capacity', { v: num(ve, 1) })}</p>}
        </section>

        <section className="panel apparatus-panel" aria-labelledby="tit-eq">
          <h3 id="tit-eq">{t('lab.equipment')}</h3>
          <svg viewBox="0 0 240 420" className="apparatus" role="img"
            aria-label={`${t('tit.added')}: ${num(added)} mL. ${t('tit.colour', { c: t(colourKey(indicator, state.pH)) })}`}>
            <defs>
              <clipPath id="flask-clip"><path d="M100 300 L100 335 L40 395 Q35 405 48 405 L192 405 Q205 405 200 395 L140 335 L140 300 Z" /></clipPath>
              <linearGradient id="glass" x1="0" x2="1"><stop offset="0" stopColor="var(--glass-edge)" /><stop offset="0.5" stopColor="var(--glass-mid)" /><stop offset="1" stopColor="var(--glass-edge)" /></linearGradient>
            </defs>
            {/* stand */}
            <rect x="20" y="410" width="200" height="8" rx="2" className="stand" />
            <rect x="24" y="10" width="6" height="402" className="stand" />
            <rect x="24" y="120" width="88" height="5" className="stand" />
            {/* burette */}
            <rect x="105" y={buretteTop} width="30" height={buretteBottom - buretteTop} fill="url(#glass)" className="glass-stroke" rx="2" />
            <rect x="107" y={level} width="26" height={Math.max(0, buretteBottom - level)} className="titrant-liquid" />
            {Array.from({ length: 11 }, (_, i) => {
              const yy = buretteTop + (i / 10) * (buretteBottom - buretteTop);
              return (
                <g key={i}>
                  <line x1="135" x2="143" y1={yy} y2={yy} className="grad" />
                  <text x="146" y={yy + 3} className="grad-text">{i * 5}</text>
                </g>
              );
            })}
            <rect x="113" y="250" width="14" height="14" className="stopcock" />
            <rect x={running ? 120 - 1.5 : 108} y={running ? 252 : 255.5} width={running ? 3 : 24} height={running ? 10 : 3} className="stopcock-handle" />
            <path d="M116 264 L124 264 L121 290 L119 290 Z" fill="url(#glass)" className="glass-stroke" />
            {drop > 0 && <circle key={drop} cx="120" cy="294" r="3" className="drop" />}
            {/* flask */}
            <g clipPath="url(#flask-clip)">
              <rect x="30" y={liquidTop} width="180" height={420 - liquidTop} className="water" />
              <rect x="30" y={liquidTop} width="180" height={420 - liquidTop} fill={liquid} />
            </g>
            <path d="M100 300 L100 335 L40 395 Q35 405 48 405 L192 405 Q205 405 200 395 L140 335 L140 300" fill="none" className="flask-outline" />
            <text x="120" y="388" textAnchor="middle" className="flask-label">{prettyFormula(analyteId)}</text>
          </svg>
          <p className="observation" aria-live="polite">{t('tit.colour', { c: t(colourKey(indicator, state.pH)) })}</p>
          <div className="btn-row">
            <button type="button" className="btn" onClick={() => setVolume(added + 1)} disabled={running || added >= maxV}>{t('tit.add', { v: num(1) })}</button>
            <button type="button" className="btn" onClick={() => setVolume(added + 0.1)} disabled={running || added >= maxV}>{t('tit.add', { v: num(0.1) })}</button>
            <button type="button" className="btn" onClick={() => setVolume(added + 0.05)} disabled={running || added >= maxV}>{t('tit.addDrop')}</button>
          </div>
          <div className="field">
            <label htmlFor="burette-slider">{t('tit.slider')}</label>
            <input id="burette-slider" type="range" min={0} max={maxV} step={0.05} value={added} disabled={running}
              onChange={(e) => setVolume(Number(e.target.value))} aria-valuetext={`${num(added)} mL`} />
          </div>
          <div className="btn-row">
            <button type="button" className="btn btn-primary" onClick={() => setRunning((r) => !r)}>{running ? t('tit.stop') : t('tit.auto')}</button>
            <button type="button" className="btn btn-ghost" onClick={() => { setRunning(false); setAdded(0); setRecorded([{ v: 0, pH: titrationState(setup, 0).pH }]); }}>{t('tit.restart')}</button>
          </div>
        </section>

        <section className="panel readouts" aria-labelledby="tit-obs" aria-live="polite">
          <h3 id="tit-obs">{t('lab.observations')}</h3>
          <div className="stats">
            <Stat label={t('tit.pH')} value={num(state.pH, 2)} emphasis />
            <Stat label={t('tit.added')} value={num(added, 2)} unit="mL" />
            <Stat label={t('tit.totalVolume')} value={num(state.totalVolume, 2)} unit="mL" />
            <Stat
              label={t('tit.ve')}
              value={revealVe ? num(ve, 2) : <button type="button" className="btn-link" onClick={() => setRevealVe(true)}>{t('tit.reveal')}</button>}
              unit={revealVe ? 'mL' : undefined}
            />
          </div>
          <p className="region"><span className="muted">{t('tit.region')}:</span> <strong>{t(regionKey)}</strong></p>

          <h4>{t('lab.calculations')}</h4>
          <ul className="calc">
            <li>{t('tit.mmol', { a: num(state.analyteMmol, 3), b: num(state.titrantMmol, 3) })}</li>
            {!weak && state.region !== 'equivalence' && state.region !== 'initial' && (
              <li>
                <code>{t('tit.eqn.excess')}</code><br />
                = {num(excessMmol, 4)} mmol / {num(state.totalVolume, 2)} mL = {num(excessMmol / state.totalVolume, 5)} mol/L
                {' → '}{state.titrantMmol > state.analyteMmol !== isBase ? `pOH = ${num(state.pOH, 2)} → pH = 14 − pOH = ${num(state.pH, 2)}` : `pH = ${num(state.pH, 2)}`}
              </li>
            )}
            {weak && <li><code>{t('tit.eqn.weak')}</code> · pKa = {num(state.pKa ?? 0, 2)}</li>}
            {state.hendersonEstimate !== null && <li>{t('tit.hh', { v: num(state.hendersonEstimate, 2) })}</li>}
            {state.region === 'equivalence' && (
              <li>{t(analyte.kind === 'weak-acid' ? 'tit.eq.weakAcid' : analyte.kind === 'weak-base' ? 'tit.eq.weakBase' : 'tit.eq.strong')}</li>
            )}
            <li className="muted">{t('tit.eqn.solver')}</li>
          </ul>
          <SaveButton onSave={() => progress.recordExperiment('titration', { analyte: analyteId, v: round2(added), pH: Math.round(state.pH * 100) / 100 })} />
        </section>
      </div>

      <section className="panel" aria-label={t('tit.chart')}>
        <div className="chart-head">
          <label className="check"><input type="checkbox" checked={showTheory} onChange={(e) => setShowTheory(e.target.checked)} /> {t('tit.theory')}</label>
          <span className="muted">{t('tit.points', { n: recorded.length })}</span>
        </div>
        <PhChart
          maxVolume={maxV}
          theory={theory}
          recorded={recorded}
          current={{ v: added, pH: state.pH }}
          equivalence={revealVe ? ve : null}
          indicatorRange={ind.range}
        />
      </section>

      <section className="panel">
        <h3>{t('lab.steps')}</h3>
        <ol className="steps">
          {(['tit.step1', 'tit.step2', 'tit.step3', 'tit.step4', 'tit.step5'] as MessageKey[]).map((k) => <li key={k}>{t(k)}</li>)}
        </ol>
      </section>
    </div>
  );
}
